import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getServerURL } from "../utils/funcs/get-serverURL";
import styles from "../styles/remedial-quote.module.scss";

type RemedialItem = {
  description: string;
  details?: string;
  price: number;
  durationMins: number;
  quantity?: number;
};

type QuoteData = {
  token: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  propertyAddress: { line1?: string; line2?: string; city?: string; postcode?: string };
  remedialWorksRequired: RemedialItem[];
  subtotalAmount: number;
  vatAmount: number;
  totalAmount: number;
  scheduledDate?: string;
  timeSlot?: "Morning" | "Afternoon";
  status: string;
  expiresAt?: string;
};

const money = (amount: number) => `£${amount.toFixed(2)}`;
const dateString = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

function getMinimumDate() {
  const date = new Date();
  date.setDate(date.getDate() + 2);
  while (date.getDay() === 0) date.setDate(date.getDate() + 1);
  return dateString(date);
}

function formatDuration(minutes: number) {
  if (!minutes) return "0 hrs";
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hr`;
}

export default function RemedialQuotePage() {
  const { token } = useParams();
  const serverURL = getServerURL();
  const normalizedToken = (token || "").match(/[a-fA-F0-9]{40}/)?.[0]?.toLowerCase() || "";
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [scheduledDate, setScheduledDate] = useState("");
  const [timeSlot, setTimeSlot] = useState<"Morning" | "Afternoon">("Morning");
  const [customerEmail, setCustomerEmail] = useState("");
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const minimumDate = getMinimumDate();

  useEffect(() => {
    let active = true;
    const fetchQuote = async () => {
      if (!normalizedToken) {
        setError("Invalid quote link");
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${serverURL}/remedial-bookings/${normalizedToken}/public`);
        if (!active) return;
        if (response.data?.success) {
          const data = response.data.quote as QuoteData;
          setQuote(data);
          setQuantities(Object.fromEntries((data.remedialWorksRequired || []).map((item, i) => [i, item.quantity || 1])));
          setCustomerEmail(data.customerEmail || "");
          setScheduledDate(data.scheduledDate || "");
          setTimeSlot(data.timeSlot || "Morning");
        } else {
          // Handle specific error cases
          if (response.data?.alreadyBooked) {
            setError(response.data?.error || "This booking has already been completed.");
          } else if (response.data?.expired) {
            setError(response.data?.error || "This quote link has expired.");
          } else {
            setError(response.data?.error || "Unable to load quote");
          }
        }
      } catch (err: any) {
        if (active) {
          // Check for specific HTTP error responses
          const errorResponse = err?.response?.data;
          if (errorResponse?.alreadyBooked) {
            setError(errorResponse?.error || "This booking has already been completed.");
          } else if (errorResponse?.expired) {
            setError(errorResponse?.error || "This quote link has expired.");
          } else {
            setError(errorResponse?.error || "Unable to load quote");
          }
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchQuote();
    return () => { active = false; };
  }, [normalizedToken, serverURL]);

  const addressLine = useMemo(() => {
    if (!quote?.propertyAddress) return "";
    const { line1, line2, city, postcode } = quote.propertyAddress;
    return [line1, line2, city, postcode].filter(Boolean).join(", ");
  }, [quote]);

  const totals = useMemo(() => {
    const subtotal = (quote?.remedialWorksRequired || []).reduce((sum, item, index) => sum + (item.price || 0) * (quantities[index] || 1), 0);
    const vat = Number((subtotal * 0.2).toFixed(2));
    return { subtotal, vat, total: Number((subtotal + vat).toFixed(2)) };
  }, [quote, quantities]);

  const handleProceedToPayment = async () => {
    if (!normalizedToken || processing) return;
    if (!customerEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim())) {
      setError("Please enter a valid email address."); return;
    }
    if (!scheduledDate) { setError("Please select a date."); return; }
    if (scheduledDate < minimumDate) {
      setError("Please select an available date at least two days from today."); return;
    }
    setProcessing(true);
    setError("");
    try {
      const response = await axios.post(`${serverURL}/remedial-bookings/${normalizedToken}/create-checkout`, {
        customerEmail: customerEmail.trim(), scheduledDate, timeSlot, quantities,
      });
      const stripeUrl = response?.data?.url;
      if (stripeUrl) window.location.href = stripeUrl;
      else setError("Unable to start payment");
    } catch (err: any) {
      setError(err?.response?.data?.error || "Unable to start payment");
    } finally { setProcessing(false); }
  };

  if (loading) return <main className={styles.container}><div className={styles.card}>Loading your quote…</div></main>;
  if (!quote) return <main className={styles.container}><div className={styles.card}><h1 className={styles.title}>Quote unavailable</h1><p>{error || "This quote link is invalid or expired."}</p></div></main>;

  return (
    <main className={styles.container}>
      <div className={styles.card}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>Remedial works quote</h1>
            <p className={styles.address}>{addressLine}</p>
            <p className={styles.customer}>{quote.customerName}{quote.customerPhone ? ` · ${quote.customerPhone}` : ""}</p>
          </div>
          {quote.expiresAt && <span className={styles.expiry}>Quote valid until {new Date(quote.expiresAt).toLocaleDateString("en-GB")}</span>}
        </header>

        <section className={styles.outcome} aria-label="Inspection outcome and next steps">
          <div className={styles.steps}>
            <div className={styles.step}>
              <span className={`${styles.iconWrap} ${styles.warning}`}><StepIcon kind="alert" /></span>
              <div><span className={styles.stepLabel}>Inspection</span><span className={styles.stepDetail}>Unsatisfactory</span></div>
            </div>
            <span className={styles.arrow} aria-hidden="true">→</span>
            <div className={styles.step}>
              <span className={styles.iconWrap}><StepIcon kind="tool" /></span>
              <div><span className={styles.stepLabel}>Remedial works</span><span className={styles.stepDetail}>Required</span></div>
            </div>
            <span className={styles.arrow} aria-hidden="true">→</span>
            <div className={styles.step}>
              <span className={styles.iconWrap}><StepIcon kind="file" /></span>
              <div><span className={styles.stepLabel}>Satisfactory EICR</span><span className={styles.stepDetail}>Included at no extra cost</span></div>
            </div>
          </div>
          <p className={styles.explanation}>Your inspection was unsatisfactory. The works below are required to resolve the identified issues. Once the installation is confirmed satisfactory, your updated EICR will be issued at no extra cost.</p>
        </section>

        <section className={styles.section} aria-labelledby="works-heading">
          <h2 id="works-heading">Required works</h2>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Work description</th>
                  <th scope="col">Duration</th>
                  <th scope="col">Price</th>
                  <th scope="col">Qty</th>
                  <th scope="col">Total</th>
                </tr>
              </thead>
              <tbody>
                {quote.remedialWorksRequired?.map((item, index) => {
                  const qty = quantities[index] || 1;
                  const rowBgColor = index % 2 === 0 ? "#ffffff" : "#f9fafb";
                  return (
                    <>
                      <tr key={`${item.description}-${index}`} style={{ backgroundColor: rowBgColor }}>
                        <td style={{ borderBottom: item.details ? "none" : "1px solid #e0eaed" }}>
                          <span className={styles.workName}>{item.description}</span>
                        </td>
                        <td style={{ borderBottom: item.details ? "none" : "1px solid #e0eaed" }}>{formatDuration(item.durationMins)}</td>
                        <td style={{ borderBottom: item.details ? "none" : "1px solid #e0eaed" }}>{money(Number(item.price || 0))}</td>
                        <td style={{ borderBottom: item.details ? "none" : "1px solid #e0eaed" }}>{qty}</td>
                        <td style={{ borderBottom: item.details ? "none" : "1px solid #e0eaed" }}>{money((item.price || 0) * qty)}</td>
                      </tr>
                      {item.details && (
                        <tr key={`${item.description}-details-${index}`} style={{ backgroundColor: rowBgColor }}>
                          <td colSpan={5} style={{ paddingTop: "4px", paddingBottom: "12px" }}>
                            <span className={styles.workDetails}>{item.details}</span>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className={styles.pricing}>
            <div><span>Subtotal</span><span>{money(totals.subtotal)}</span></div>
            <div><span>VAT (20%)</span><span>{money(totals.vat)}</span></div>
            <div className={styles.grandTotal}><span>Total including VAT</span><span>{money(totals.total)}</span></div>
          </div>
        </section>

        <section className={styles.section} aria-labelledby="booking-heading">
          <h2 id="booking-heading">Choose your appointment</h2>
          <div className={styles.formGroup}>
            <label htmlFor="remedial-email">Email address *</label>
            <input id="remedial-email" type="email" autoComplete="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="you@example.com" required />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="remedial-date">Preferred date *</label>
              <DatePicker
                id="remedial-date"
                selected={scheduledDate ? new Date(`${scheduledDate}T12:00:00`) : null}
                onChange={(date) => {
                  if (date) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    setScheduledDate(`${year}-${month}-${day}`);
                    setError("");
                  }
                }}
                minDate={new Date(`${minimumDate}T12:00:00`)}
                filterDate={(date) => date.getDay() !== 0}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select a date"
                required
              />
            </div>
            <div className={styles.formGroup}><label htmlFor="remedial-slot">Arrival window</label><select id="remedial-slot" value={timeSlot} onChange={e => setTimeSlot(e.target.value as "Morning" | "Afternoon")}><option value="Morning">Morning · 8:00 am – 1:00 pm</option><option value="Afternoon">Afternoon · 1:00 pm – 6:00 pm</option></select></div>
          </div>
          <p className={styles.bookingNote}>Your booking is confirmed after successful payment. Confirmation will be emailed to you.</p>
          {error && <div className={styles.alertError} role="alert">{error}</div>}
          <button className={styles.cta} type="button" onClick={handleProceedToPayment} disabled={processing || !customerEmail.trim()}>{processing ? "Redirecting to secure payment…" : `Pay ${money(totals.total)} securely →`}</button>
          <p className={styles.stripeNote}>Secure payment via Stripe · VAT included</p>
        </section>
        <div className={styles.spacer}></div>
      </div>
    </main>
  );
}

function StepIcon({ kind }: { kind: "alert" | "tool" | "file" }) {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {kind === "alert" ? <><circle cx="12" cy="12" r="9"/><path d="M12 7v6M12 17h.01"/></> : kind === "tool" ? <><path d="M14.5 6.3a5 5 0 0 0-6.2 6.2l-5.4 5.4a2 2 0 0 0 2.8 2.8l5.4-5.4a5 5 0 0 0 6.2-6.2l-3.2 3.2-2.4-2.4z"/></> : <><path d="M13 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10z"/><path d="M13 3v7h7M8 15l2.5 2.5L16 12"/></>}
  </svg>;
}
